import { useMutation } from '@apollo/client';
import { Formik } from 'formik';
import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { REGISTER_BRAND_USER } from '../../../graphql/REGISTER_BRAND_USER';
import Api from '../../../services/api';
import FormField from '../../FormField';

const brandSignUpValidationSchema = Yup.object().shape({
    agreement: Yup.boolean()
        .oneOf([true], 'Must Accept Terms and Conditions')
        .required('Must Accept Terms and Conditions'),
    email: Yup.string()
        .email('Invalid email')
        .required('This field is required'),
    name: Yup.string().required('This field is required'),
    password: Yup.string()
        .min(8, 'Password should have at least 8 characters.')
        .max(24, 'Password should not exceed 24 characters.')
        .required('This field is required'),
    passwordConfirm: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords does not match.')
        .required('This field is required')
});

const BrandSignUpForm = () => {
    const history = useHistory();

    const onCompleted = async ({ registerUser }) => {
        try {
            const loginResponse = await Api.loginByEmail({
                email: registerUser.email,
                password: registerUser.password
            });

            if (loginResponse) {
                toast.success('Brand account created successfully!');
                setTimeout(() => {
                    history.push('/brand-onboarding');
                }, 500);
            }
        } catch (error) {
            console.error('Auto-login error:', error);
            toast.success('Brand account created! Please log in to continue.');
            history.push('/sign-in');
        }
    };

    const onError = (error: Error) =>
        toast.error(`Cannot create brand account: ${error.message}`, {
            autoClose: false
        });

    const [signUpBrand] = useMutation(REGISTER_BRAND_USER, {
        onCompleted,
        onError
    });

    return (
        <Formik
            initialValues={{
                agreement: '',
                email: '',
                name: '',
                password: '',
                passwordConfirm: ''
            }}
            validationSchema={brandSignUpValidationSchema}
            onSubmit={async (values, { setSubmitting }) => {
                await signUpBrand({
                    variables: {
                        ...values,
                        userType: 'BRAND'
                    }
                });
                setSubmitting(false);
            }}
        >
            {({ handleSubmit, isSubmitting, values, errors, setFieldValue }) => (
                <form onSubmit={handleSubmit} className="form-box">
                    <header className="content has-text-centered">
                        <h3 className="form-box-title">Brand Sign up</h3>
                        <p className="is-size-7 has-text-black">Create your brand account</p>
                    </header>
                    <div className="field">
                        <div className="field-body">
                            <FormField name="name" type="text" placeholder="Brand Name" />
                        </div>
                    </div>
                    <div className="field">
                        <div className="field-body">
                            <FormField name="email" type="email" placeholder="Business Email" />
                        </div>
                    </div>
                    <div className="field">
                        <div className="field-body">
                            <FormField
                                name="password"
                                type="password"
                                placeholder="Password"
                            />
                        </div>
                    </div>
                    <div className="field">
                        <div className="field-body">
                            <FormField
                                name="passwordConfirm"
                                type="password"
                                placeholder="Confirm Password"
                            />
                        </div>
                    </div>
                    <div className="page-sign-up-form-terms">
                        <input
                            className="page-sign-up-form-terms-check"
                            type="checkbox"
                            name="agreement"
                            onChange={async () => await setFieldValue('agreement', !values.agreement)}
                            checked={Boolean(values.agreement)}
                        />
                        <p className="page-sign-up-form-terms-links">
                            I agree to the <Link to="/terms-of-use">Terms of Use</Link> and{' '}
                            <Link to="privacy-policy">Privacy Policy</Link>
                        </p>
                    </div>
                    <span className="field-error has-text-danger">
                        {errors && errors.agreement}
                    </span>

                    <div className="field field-login">
                        <button
                            type="submit"
                            className={`button is-secondary has-text-weight-bold ${isSubmitting && 'is-loading'}`}
                            disabled={isSubmitting}
                        >
                            Create Brand Account
                        </button>
                        <p className="has-text-centered">
                            Already have an account?{' '}
                            <Link to="/sign-in">
                                <span className="has-text-weight-bold">Log in</span>
                            </Link>
                        </p>
                    </div>
                </form>
            )}
        </Formik>
    );
};

export default BrandSignUpForm;
